import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { ToastContainer, toast } from "react-toastify";
import { BsFillSuitHeartFill } from "react-icons/bs";

const POPULAR_GEMSTONES = [
  { label: "Ruby", color: "#e53935", bg: "#fdecea" },
  { label: "Sapphire", color: "#1565c0", bg: "#e3f2fd" },
  { label: "Emerald", color: "#2e7d32", bg: "#e8f5e9" },
  { label: "Diamond", color: "#757575", bg: "#f5f5f5" },
  { label: "Yellow Sapphire", color: "#f9a825", bg: "#fffde7" },
  { label: "Red Coral", color: "#c62828", bg: "#fce4ec" },
];

const RECENT_SEARCHES = [
  "Ruby",
  "Sapphire",
  "Emerald",
  "Yellow Sapphire",
  "Diamond",
];

const GemSVG = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
    <path d="M12 2L2 9l10 13L22 9z" />
  </svg>
);

const HistorySVG = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#999">
    <path d="M13 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7v4l5-5-5-5v4z" />
  </svg>
);

const ChevronSVG = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ccc"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const BackSVG = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#333">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);

const SearchIconSVG = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#aaa">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#fff",
    fontFamily: "'Segoe UI', sans-serif",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    borderBottom: "1px solid #e0e0e0",
    gap: 10,
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 10,
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    borderRadius: "50%",
  },
  searchBar: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f2f2f2",
    borderRadius: 25,
    padding: "7px 14px",
    gap: 8,
  },
  searchInput: {
    border: "none",
    background: "none",
    outline: "none",
    fontSize: 15,
    color: "#333",
    flex: 1,
    width: "100%",
  },
  spinner: {
    width: 16,
    height: 16,
    border: "2px solid #ddd",
    borderTop: "2px solid #999",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    flexShrink: 0,
  },
  body: {
    padding: "16px 16px 24px",
    height: "100vh",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111",
    margin: "0 0 12px 0",
  },
  recentList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  recentItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 4px",
    borderBottom: "1px solid #f5f5f5",
    cursor: "pointer",
  },
  recentIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "#f2f2f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  recentLabel: {
    flex: 1,
    fontSize: 15,
    color: "#333",
  },
  divider: {
    height: 1,
    backgroundColor: "#ececec",
    margin: "20px 0",
  },
  gemGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  gemCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderRadius: 14,
    padding: "12px 14px",
    cursor: "pointer",
    border: "none",
    transition: "transform 0.15s, box-shadow 0.15s",
    width: "100%",
    textAlign: "left",
  },
  gemIconCircle: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "rgba(255,255,255,0.5)",
  },
  gemLabel: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
  },
  resultItem: {
    display: "flex",
    alignItems: "center",
    padding: "13px 4px",
    borderBottom: "1px solid #f5f5f5",
    cursor: "pointer",
    gap: 8,
  },
  resultTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: "#222",
    margin: "0 0 2px 0",
  },
  resultSub: {
    fontSize: 12,
    color: "#888",
    margin: 0,
  },
  noResult: {
    textAlign: "center",
    marginTop: 40,
    color: "#aaa",
    fontSize: 15,
  },
};

const Search = ({ onBack }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(RECENT_SEARCHES);
  const debounceRef = useRef(null);
  let _id = secureLocalStorage.getItem("n_id");

  const fetchResults = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = { product_name: searchQuery };
      const res = await axios.post(
        `${process.env.REACT_APP_API}website/search_product_name`,
        data,
      );
      setResults(res?.data?.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleRecentClick = (term) => setQuery(term);

  const handlePopularClick = (label) => {
    setQuery(label);
    setRecentSearches((prev) =>
      [label, ...prev.filter((r) => r !== label)].slice(0, 5),
    );
  };

  const showResults = query.trim().length > 0;

  const handleBack = () => {
    setResults([]);
    setQuery("");
    if (!showResults) {
      navigate(-1);
    }
  };

  let Single_products = (productId) => {
    navigate(`/single_products/${productId}`);
  };

  let handleLogin = () => {
    navigate(`/login`);
  };

  const addfevrate = async (productsid) => {
    try {
      const items = {
        user_id: _id,
        product_id: productsid,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API}website/add_favourite_product`,
        items,
      );

      if (res.data.result) {
        toast.success(res.data.msg);
      } else {
        toast.error(res.data.msg || "Something went wrong");
      }
    } catch (error) {
      toast.error("Product removed from favourites successfully.");
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .recent-item:hover { background: #fafafa; }
        .gem-card:hover { transform: scale(1.03); box-shadow: 0 4px 14px rgba(0,0,0,0.09); }
        .result-item:hover { background: #fafafa; }
      `}</style>

      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={handleBack}>
            <BackSVG />
          </button>
          <div style={styles.searchBar}>
            <SearchIconSVG />
            <input
              style={styles.searchInput}
              placeholder="Search gemstones..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {loading && <div style={styles.spinner} />}
          </div>
        </div>

        <div style={styles.body}>
          {showResults ? (
            <>
              <p style={styles.sectionTitle}>Search Results</p>
              {!loading && results.length === 0 ? (
                <p style={styles.noResult}>No results found for "{query}"</p>
              ) : (
                <div
                  className="vertical-scroll-container"
                  style={{ marginBottom: "20px" }}
                >
                  <div className="row g-3">
                    {results?.map((items) => {
                      return (
                        <div className="new-arrival-carousel-item swiper-slide col-xl-3 col-lg-3 col-md-4 col-6">
                          <div
                            className="product-card-list"
                            style={{ marginRight: "-10px", cursor: "pointer" }}
                          >
                            <div
                              className="product-card"
                              onClick={() => Single_products(items.product_id)}
                            >
                              <div className="product-thumb-nail">
                                <span>
                                  <img
                                    className="product-image p-2"
                                    src={`${process.env.REACT_APP_API}uploads/${items.product_image}`}
                                    alt="image_not_found"
                                  />
                                </span>
                                <ul className="actions">
                                  {_id ? (
                                    items.favourite_status == 1 ? (
                                      <li className="action whish-list">
                                        <BsFillSuitHeartFill
                                          style={{ color: "red" }}
                                          onClick={() => addfevrate(items._id)}
                                        />
                                      </li>
                                    ) : (
                                      <li
                                        className="action whish-list"
                                        onClick={() =>
                                          addfevrate(
                                            items._id,
                                            items.favourite_status,
                                          )
                                        }
                                      >
                                        <i className="lar la-heart" />
                                      </li>
                                    )
                                  ) : (
                                    <li className="action whish-list">
                                      <button>
                                        <a href="/login">
                                          <i className="lar la-heart" />
                                        </a>
                                      </button>
                                    </li>
                                  )}

                                  <li className="action quick-view">
                                    {_id ? (
                                      <button
                                        data-toggle="modal"
                                        data-target="#dealsofdaymodal"
                                      >
                                        <i
                                          className="las la-eye"
                                          onClick={() =>
                                            Single_products(items.product_id)
                                          }
                                        />
                                      </button>
                                    ) : (
                                      <a href="/login">
                                        <button>
                                          <i className="las la-eye" />
                                        </button>
                                      </a>
                                    )}
                                  </li>
                                </ul>
                              </div>

                              <div className="product-content">
                                <h4 className="product-sub-title">
                                  <a href="#">{items.category_name}</a>
                                </h4>
                                {_id ? (
                                  <a href="#">
                                    <h3
                                      className="product-title"
                                      onClick={() => Single_products(items.product_id)}
                                    >
                                      {items.product_name}
                                    </h3>
                                  </a>
                                ) : (
                                  <a href="#">
                                    <h3
                                      className="product-title"
                                      onClick={() => handleLogin(items._id)}
                                    >
                                      {items.product_name}
                                    </h3>
                                  </a>
                                )}

                                {/* Product Specifications */}
                                <div className="product-specs mb-2">
                                  <div className="spec-item d-flex justify-content-between">
                                    <span className="text-black">Weight:</span>
                                    <span className="text-muted">
                                      {items.weight || "N/A"}
                                    </span>
                                  </div>
                                  <div className="spec-item d-flex justify-content-between">
                                    <span className="text-black">Shape:</span>
                                    <span className="text-muted">
                                      {items.shape || "N/A"}
                                    </span>
                                  </div>
                                  <div className="spec-item d-flex justify-content-between">
                                    <span className="text-black">Origin:</span>
                                    <span className="text-muted">
                                      {items.origin || "N/A"}
                                    </span>
                                  </div>
                                </div>

                                {/* Price Section */}
                                <div className="product-price-wrapp  mb-3">
                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    {items.available_status?.includes(
                                      "Buy",
                                    ) && (
                                      <>
                                        <span className="text-black">
                                          Best Buy:
                                        </span>

                                        <span
                                          style={{
                                            fontSize: "13px",
                                            fontWeight: "700",
                                            color: "black",
                                          }}
                                        >
                                          ₹{items.sale_price?.toLocaleString()}
                                        </span>
                                      </>
                                    )}
                                  </div>

                                  <p
                                    style={{
                                      color: "#ff5722",
                                      marginTop: "5px",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    OR
                                  </p>

                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="text-black">
                                      Leasing starts from
                                    </span>

                                    <span
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "700",
                                        color: "#ff5722",
                                      }}
                                    >
                                      ₹{items.lease_price?.toLocaleString()}
                                    </span>
                                  </div>

                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="text-black">
                                      Security Deposit
                                    </span>

                                    <span
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "700",
                                        color: "black",
                                      }}
                                    >
                                      ₹{items.rent_price?.toLocaleString()}
                                    </span>
                                  </div>
                                </div>

                                <div className="product-cart-btn-wrap">
                                  <button
                                    onClick={() => Single_products(items.product_id)}
                                    className="btn btn-success add-to-cart-btn btn-lg w-100 py-3"
                                  >
                                    View Details
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Recent Searches */}
              <p style={styles.sectionTitle}>Recent Searches</p>
              <ul style={styles.recentList}>
                {recentSearches.map((term) => (
                  <li
                    key={term}
                    className="recent-item"
                    style={styles.recentItem}
                    onClick={() => handleRecentClick(term)}
                  >
                    <span style={styles.recentIcon}>
                      <HistorySVG />
                    </span>
                    <span style={styles.recentLabel}>{term}</span>
                    <ChevronSVG />
                  </li>
                ))}
              </ul>

              <div style={styles.divider} />

              {/* Popular Gemstones */}
              <p style={styles.sectionTitle}>Popular Gemstones</p>
              <div style={styles.gemGrid}>
                {POPULAR_GEMSTONES.map((gem) => (
                  <button
                    key={gem.label}
                    className="gem-card"
                    style={{ ...styles.gemCard, backgroundColor: gem.bg }}
                    onClick={() => handlePopularClick(gem.label)}
                  >
                    <span style={styles.gemIconCircle}>
                      <GemSVG color={gem.color} />
                    </span>
                    <span style={{ ...styles.gemLabel, color: gem.color }}>
                      {gem.label}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Search;
